import { Dispatch, Reducer } from "react";
import { produce } from "immer";

import { AppState } from "./types";
import { pieceKind } from "../types/game";
import { Piece } from "../game/mechanics";
import { game } from "../game/config";

export type ActionType<Action, Payload = void> = Payload extends void
  ? { type: Action }
  : { type: Action; payload: Payload };

export type AppActions =
  // _______ APP ACTIONS _______
  | ActionType<"SET_USER_UUID", { uuid: string }>

  // _______ GAME ACTIONS _______
  | ActionType<
      "INITIALIZE_GAME",
      {
        players: string[];
        onlineGameId: string | null;
        localPlayerId: number | null;
      }
    >
  | ActionType<"CANCEL_ACTION">
  | ActionType<"SET_SELECTED_PIECE_TO_PLACE", { kind: pieceKind }>
  | ActionType<"SET_SELECTED_PIECE_TO_MOVE", { piece: Piece }>
  | ActionType<"SET_MOVE_PREVIEW", { piece: Piece | null }>
  | ActionType<"NEXT_TURN", { justPlaced: pieceKind | undefined }>
  | ActionType<"FINISH_GAME">;

export type AppDispatch = Dispatch<AppActions>;

export const appStateReducer: Reducer<AppState, AppActions> = produce(
  (draft: AppState, action: AppActions) => {
    switch (action.type) {
      // _______ APP ACTIONS _______
      case "SET_USER_UUID": {
        draft.userUuid = action.payload.uuid;
        break;
      }

      // _______ GAME ACTIONS _______
      case "INITIALIZE_GAME": {
        draft.onGoingGame = true;
        draft.game = game(
          action.payload.players,
          action.payload.onlineGameId,
          action.payload.localPlayerId
        );
        break;
      }
      case "CANCEL_ACTION": {
        draft.game.action.placing = null;
        draft.game.action.moving = null;
        break;
      }

      case "SET_SELECTED_PIECE_TO_PLACE": {
        draft.game.action.moving = null;
        draft.game.action.placing = action.payload.kind;
        break;
      }
      case "SET_SELECTED_PIECE_TO_MOVE": {
        draft.game.action.placing = null;
        draft.game.action.moving = action.payload.piece;
        break;
      }
      case "SET_MOVE_PREVIEW": {
        draft.game.action.previewingMoveFor = action.payload.piece;
        break;
      }
      case "NEXT_TURN": {
        console.log("action.payload.justPlaced", action.payload.justPlaced);

        // Decrement roster on place
        const currentPlayer = draft.game.players.find(
          (p) => draft.game.turnOfPlayer.id === p.id
        );
        if (!currentPlayer) {
          throw new Error("Missing player!");
        }
        if (action.payload.justPlaced) {
          currentPlayer.piecesRoster[action.payload.justPlaced] -= 1;
        }

        // Reset action
        draft.game.action.moving = null;
        draft.game.action.placing = null;

        // Increment turn
        const nextPlayer = draft.game.players.find(
          (p) => p.id === draft.game.turnOfPlayer.id + 1
        );
        if (nextPlayer) {
          draft.game.turnOfPlayer = nextPlayer;
        } else {
          draft.game.turn += 1;
          draft.game.turnOfPlayer = draft.game.players[0];
        }

        // Check if players lost and endgame condition
        const playersLost = draft.game.board.checkIfPlayerLost();
        if (playersLost.length) {
          const remaining = draft.game.players.filter(
            (p) => !playersLost.some((p2) => p2.id === p.id)
          );
          if (remaining.length <= 1) {
            draft.displayGameResults = true;
            if (remaining.length === 1) {
              draft.game.winner = remaining[0];
            }
          }
          // TODO: for 3+ players, 'deactivate' all losers progressively
          console.log(
            "PLAYER LOST",
            playersLost.map((p) => p.name)
          );
        }
        break;
      }
      case "FINISH_GAME": {
        draft.onGoingGame = false;
        draft.displayGameResults = false;
        break;
      }
      default:
        throw new Error("Invalid action dispatched in appContext");
    }
  }
);
